import { useEffect, useRef } from 'react';
import type { CallState } from './useWebRTC';

const RING_HZ = 425;      // стандартный длинный гудок
const RING_ON_MS = 1000;
const RING_OFF_MS = 4000;

/**
 * Удобства на время звонка:
 *  - экран телефона не уходит в блокировку (Screen Wake Lock), пока звонок идёт;
 *  - при исходящем вызове, до ответа абонента, играет гудок (425 Гц: 1 с звук, 4 с пауза).
 */
export function useCallComfort(callState: CallState) {
  const busy = callState === 'calling' || callState === 'ringing' || callState === 'active';
  const dialing = callState === 'calling';

  // ── не давать экрану гаснуть ──
  const lockRef = useRef<any>(null);
  useEffect(() => {
    if (!busy) return;
    const nav: any = navigator;
    if (!nav.wakeLock?.request) return;
    let cancelled = false;

    const acquire = async () => {
      if (cancelled || document.visibilityState !== 'visible' || lockRef.current) return;
      try {
        const lock = await nav.wakeLock.request('screen');
        if (cancelled) { lock.release().catch(() => {}); return; }
        lockRef.current = lock;
        lock.addEventListener('release', () => { if (lockRef.current === lock) lockRef.current = null; });
      } catch { /* нет разрешения или режим экономии — не критично */ }
    };
    // после возврата на вкладку система снимает блокировку — берём заново
    const onVisible = () => { if (document.visibilityState === 'visible') acquire(); };
    document.addEventListener('visibilitychange', onVisible);
    acquire();

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      const l = lockRef.current;
      lockRef.current = null;
      l?.release?.().catch(() => {});
    };
  }, [busy]);

  // ── гудок исходящего вызова ──
  useEffect(() => {
    if (!dialing) return;
    const AC = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AC) return;
    let stopped = false;
    let ctx: AudioContext | null = null;
    let osc: OscillatorNode | null = null;
    let gain: GainNode | null = null;
    let timer: number | undefined;

    try {
      const c: AudioContext = new AC();
      ctx = c;
      c.resume?.().catch(() => {});
      const g = c.createGain();
      g.gain.value = 0;
      g.connect(c.destination);
      gain = g;
      const o = c.createOscillator();
      o.type = 'sine';
      o.frequency.value = RING_HZ;
      o.connect(g);
      o.start();
      osc = o;
    } catch { return; }

    const beep = () => {
      const c = ctx, g = gain;
      if (stopped || !c || !g) return;
      const t = c.currentTime;
      g.gain.cancelScheduledValues(t);
      g.gain.setTargetAtTime(0.18, t, 0.01);
      g.gain.setTargetAtTime(0, t + RING_ON_MS / 1000, 0.01);
      timer = window.setTimeout(beep, RING_ON_MS + RING_OFF_MS);
    };
    beep();

    return () => {
      stopped = true;
      if (timer) clearTimeout(timer);
      try { osc?.stop(); } catch { /* ignore */ }
      try { ctx?.close(); } catch { /* ignore */ }
    };
  }, [dialing]);
}
